/**
 * Hush lantern v2 (`hush.lantern.v2`) — a lantern that reads as a lantern.
 *
 * Plan: docs/plans/2026-10-22-hush-hideout-redesign/03-LANTERN-REDESIGN.md. The v1 kit lantern (a pale flat
 * paper body with two holes over one) read as a face beside the game's ghosts; v1 stays in hushStatic for the
 * scarecrow fork. v2 keeps Pip's two-over-one windows but sets them inside real lantern anatomy:
 *   ring + cord (hanging) → peaked cap → dark corner posts and rails → mid-value paper → three lit panes split by
 *   mullions → bottom rail and drip tray → feet + contact shadow (standing only).
 * Value order, dark → light: frame/cap < paper < window cores. The base layer alone (effects off) still reads as a
 * lamp; `lantern-emissive` (bloom, bright cores, flame) is separate so light can be driven or removed.
 *
 * Drawn directly in template-frame pixels (≈0.75 of a 79 px ghost body). No collision or gameplay geometry.
 */
import { SeededRng } from '../../utils/SeededRng';
import type { PaletteRole, ResolvedPalette } from '../hushStatic/contracts';

export const HUSH_LANTERN_V2_REVISION = 'hush.lantern.v2/revision-01';

export type LanternSupport = 'hanging' | 'standing';
export type LanternCondition = 'salt_worn' | 'dry' | 'interior_warm';

export interface LanternV2Recipe {
  readonly schema: 'hush.asset-recipe.v1';
  readonly id: string;
  readonly generatorId: 'hush.lantern.v2';
  readonly seed: number;
  readonly paletteId: 'coastal_salt' | 'nook_warm';
  readonly params: {
    readonly support: LanternSupport;
    readonly condition: LanternCondition;
    readonly warmth: number;         // 0 banked … 1 fully lit
    readonly repairPatch: boolean;   // lower-left paper repair
  };
}

type Point = { readonly x: number; readonly y: number };
export interface LanternV2Anchors {
  readonly hang?: Point;
  readonly support: Point;
  readonly ribbon_tie: Point;
  readonly light_core: Point;
  readonly mote_origin: Point;
  readonly windows: readonly [Point, Point, Point];
}
export interface LanternV2Art {
  readonly frame: { readonly w: number; readonly h: number };
  readonly body: string;
  readonly anchors: LanternV2Anchors;
  /** Sample points (frame px) for the value-order check: a frame member, open paper, each window core. */
  readonly probes: { readonly frame: Point; readonly paper: Point; readonly windows: readonly Point[] };
  readonly parts: readonly string[];
}

export const LANTERN_V2_FRAMES: Readonly<Record<LanternSupport, { w: number; h: number }>> = {
  hanging: { w: 48, h: 72 },
  standing: { w: 48, h: 62 },
};

/** Two over one, in hanging-frame pixels (standing shifts by its y offset). Pip's riddle depends on this. */
export const LANTERN_V2_WINDOWS = [{ x: 18.75, y: 31, r: 4 }, { x: 29.25, y: 31, r: 4 }, { x: 24, y: 46, r: 4.3 }] as const;
const LIGHT_CORE = { x: 24, y: 40 };
/** Window centers relative to `light_core` (same for both supports) — drives the discovery wink. */
export const LANTERN_V2_WINDOW_OFFSETS = LANTERN_V2_WINDOWS.map(w => ({ x: w.x - LIGHT_CORE.x, y: w.y - LIGHT_CORE.y, r: w.r }));

const n = (v: number): string => {
  if (!Number.isFinite(v)) throw new Error('hush lantern v2: nonfinite geometry');
  return String(Math.round(v * 1000) / 1000);
};
type Attrs = Record<string, string | number | undefined>;
function el(tag: string, attrs: Attrs, inner = ''): string {
  const a = Object.entries(attrs).filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${k}="${typeof v === 'number' ? n(v) : v}"`).join(' ');
  return inner ? `<${tag} ${a}>${inner}</${tag}>` : `<${tag} ${a}/>`;
}

function hexMix(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16), pb = parseInt(b.slice(1), 16);
  const ch = (s: number) => Math.round(((pa >> s) & 255) + (((pb >> s) & 255) - ((pa >> s) & 255)) * t);
  return '#' + ((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, '0');
}

export function validateLanternV2Recipe(input: unknown): LanternV2Recipe {
  const r = input as LanternV2Recipe;
  const fail = (m: string): never => { throw new Error(`hush lantern v2 recipe: ${m}`); };
  if (!r || typeof r !== 'object' || r.schema !== 'hush.asset-recipe.v1' || r.generatorId !== 'hush.lantern.v2') fail('wrong schema/generator');
  if (typeof r.id !== 'string' || !Number.isInteger(r.seed)) fail('id/seed');
  if (r.paletteId !== 'coastal_salt' && r.paletteId !== 'nook_warm') fail('paletteId');
  const p = r.params;
  if (!p || (p.support !== 'hanging' && p.support !== 'standing')) fail('support');
  if (!['salt_worn', 'dry', 'interior_warm'].includes(p.condition)) fail('condition');
  if (typeof p.warmth !== 'number' || !(p.warmth >= 0 && p.warmth <= 1)) fail('warmth must be 0..1');
  if (typeof p.repairPatch !== 'boolean') fail('repairPatch');
  return r;
}

/** Build the lantern in its template frame. `scope` prefixes every SVG definition id. */
export function buildLanternV2(input: LanternV2Recipe, roles: ResolvedPalette, scope: string): LanternV2Art {
  const recipe = validateLanternV2Recipe(input);
  if (!/^[a-z0-9_-]{1,80}$/i.test(scope)) throw new Error('hush lantern v2: unsafe definition scope');
  const { support, condition, warmth, repairPatch } = recipe.params;
  const c = (role: PaletteRole) => roles[role];
  const rng = (channel: string) => new SeededRng(`${recipe.seed}|${recipe.id}|${channel}`).next();
  const oy = support === 'hanging' ? 0 : -7;
  const Y = (y: number) => y + oy;
  const frame = LANTERN_V2_FRAMES[support];
  const parts: string[] = [];
  const g = (part: string, inner: string, extra: Attrs = {}) => { parts.push(part); return el('g', { 'data-part': part, ...extra }, inner); };

  // Mid-value paper: never the brightest thing on the lamp.
  const paperTop = condition === 'dry' ? hexMix(c('paperBase'), c('paperLight'), 0.25)
    : condition === 'interior_warm' ? hexMix(c('paperBase'), c('warmCore'), 0.2) : c('paperBase');
  const paperBottom = hexMix(c('paperBase'), c('paperShadow'), condition === 'dry' ? 0.35 : 0.55);
  const frameDark = c('woodShadow');
  const outline = c('outline');
  const pane = hexMix(c('warmCore'), c('paperLight'), 0.25);
  const core = hexMix(c('warmCore'), '#ffffff', 0.55);
  const flame = hexMix(c('warmCore'), '#e8892c', 0.6);
  const tilt = (rng('cap.tilt') - 0.5) * 5;

  const gradId = `${scope}_paper`, bloomId = `${scope}_bloom`, clipId = `${scope}_panel`;
  const defs = el('defs', {},
    el('linearGradient', { id: gradId, x1: 0, y1: Y(23), x2: 0, y2: Y(59), gradientUnits: 'userSpaceOnUse' },
      el('stop', { offset: 0, 'stop-color': paperTop }) + el('stop', { offset: 1, 'stop-color': paperBottom }))
    + el('radialGradient', { id: bloomId },
      el('stop', { offset: 0, 'stop-color': c('warmCore'), 'stop-opacity': 0.95 })
      + el('stop', { offset: 1, 'stop-color': c('warmCore'), 'stop-opacity': 0 }))
    + el('clipPath', { id: clipId }, el('rect', { x: 13, y: Y(23), width: 22, height: 36 })));

  // ---- base: paper panel, lit panes, paper wear and repair ----
  let paper = el('rect', { x: 13, y: Y(23), width: 22, height: 36, fill: `url(#${gradId})` });
  let fibres = '';
  for (let i = 0; i < 6; i++) {
    const x = 14 + rng(`fibre.${i}.x`) * 17, y = Y(41 + rng(`fibre.${i}.y`) * 16);
    fibres += el('path', { d: `M${n(x)} ${n(y)}q2.5 -0.6 5 0.4`, stroke: c('paperShadow'), 'stroke-width': 0.5, fill: 'none', opacity: 0.35 });
  }
  paper += el('g', { 'clip-path': `url(#${clipId})` }, fibres);
  paper += el('path', { d: `M24 ${n(Y(40))}V${n(Y(59))}`, stroke: c('paperShadow'), 'stroke-width': 0.6, opacity: 0.5 });
  if (repairPatch) {
    paper += el('path', { d: `M14.6 ${n(Y(51.6))}L19.8 ${n(Y(50.8))}L20.3 ${n(Y(57))}L15.2 ${n(Y(57.6))}Z`,
      fill: hexMix(paperTop, c('paperLight'), 0.4), stroke: c('outlineSoft'), 'stroke-width': 0.6 });
    // Stitches run in one row along the top seam: scattered dots inside a pale patch read as a little face.
    paper += el('path', { d: `M15.4 ${n(Y(52.2))}l0.7 -1M17.2 ${n(Y(51.9))}l0.7 -1M19 ${n(Y(51.7))}l0.7 -1`,
      stroke: c('woodShadow'), 'stroke-width': 0.55 });
  }
  const panes = LANTERN_V2_WINDOWS.map((w, i) => el('circle', {
    cx: w.x, cy: Y(w.y), r: w.r, fill: pane, stroke: c('paperShadow'), 'stroke-width': 0.6, 'data-window-index': i,
  })).join('');
  const base = g('lantern-paper', paper) + g('lantern-panes', panes);

  // ---- emissive: bloom, bright cores, a small flame and wick in the lower pane ----
  const glow = 0.3 + 0.5 * warmth;
  let light = LANTERN_V2_WINDOWS.map(w => el('circle', { cx: w.x, cy: Y(w.y), r: w.r * 1.9, fill: `url(#${bloomId})`, opacity: n(glow) })).join('');
  light += LANTERN_V2_WINDOWS.map(w => el('circle', { cx: w.x, cy: Y(w.y), r: w.r * 0.62, fill: core, opacity: n(0.55 + 0.45 * warmth) })).join('');
  const lw = LANTERN_V2_WINDOWS[2];
  light += el('path', { d: `M24 ${n(Y(lw.y - 3.4))}C25.9 ${n(Y(lw.y - 1))} 26 ${n(Y(lw.y + 1.6))} 24 ${n(Y(lw.y + 2.4))}`
    + `C22 ${n(Y(lw.y + 1.6))} 22.1 ${n(Y(lw.y - 1))} 24 ${n(Y(lw.y - 3.4))}Z`, fill: flame, opacity: n(0.6 + 0.4 * warmth) });
  light += el('path', { d: `M24 ${n(Y(lw.y + 2.2))}V${n(Y(lw.y + 3.6))}`, stroke: outline, 'stroke-width': 0.8 });
  const emissive = g('lantern-emissive', el('g', { 'clip-path': `url(#${clipId})` }, light));

  // ---- frame: mullions, posts, rails, cap, tray, ring or feet (darkest values, drawn last) ----
  const member = (x: number, y: number, w: number, h: number, fill = frameDark) =>
    el('rect', { x, y: Y(y), width: w, height: h, fill, stroke: outline, 'stroke-width': 0.8 });
  let frameMarkup = member(23.2, 23, 1.6, 15.2) + member(13, 38, 22, 1.7);
  frameMarkup += member(10.5, 20, 3, 42) + member(34.5, 20, 3, 42);
  frameMarkup += member(11, 20, 26, 3, c('woodBase')) + member(11, 59, 26, 3, c('woodBase'));
  frameMarkup += el('path', { d: `M8 ${n(Y(62))}H40L38 ${n(Y(65))}H10Z`, fill: frameDark, stroke: outline, 'stroke-width': 1 });
  const cap = el('path', { d: `M8.5 ${n(Y(20.5))}L24 ${n(Y(11))}L39.5 ${n(Y(20.5))}Z`, fill: frameDark, stroke: outline, 'stroke-width': 1.2 })
    + el('path', { d: `M14 ${n(Y(17.4))}L24 ${n(Y(12.6))}`, stroke: c('woodLight'), 'stroke-width': 0.9, opacity: 0.6 })
    + el('circle', { cx: 24, cy: Y(10.6), r: 1.6, fill: c('woodBase'), stroke: outline, 'stroke-width': 0.6 });
  frameMarkup += el('g', { transform: `rotate(${n(tilt)} 24 ${n(Y(20))})` }, cap);
  if (condition === 'salt_worn') {
    frameMarkup += el('path', { d: `M12 ${n(Y(19))}l3 -1.6M30.5 ${n(Y(14.6))}l2.6 1.4M11.5 ${n(Y(61))}h4M31 ${n(Y(63.4))}h4`,
      stroke: c('salt'), 'stroke-width': 0.7, opacity: 0.75 });
  }
  if (support === 'hanging') {
    frameMarkup += el('path', { d: 'M22 65L24 68.6L26 65Z', fill: outline });
    frameMarkup += el('path', { d: 'M24 8V11', stroke: outline, 'stroke-width': 1.4 });
    frameMarkup += el('circle', { cx: 24, cy: 5, r: 3, fill: 'none', stroke: outline, 'stroke-width': 1.6 });
  } else {
    // Contact shadow sits just above the ground line so no paint leaves the frame.
    frameMarkup = el('ellipse', { cx: 24, cy: Y(67.8), rx: 16, ry: 1.2, fill: outline, opacity: 0.2 })
      + frameMarkup + member(12, 65, 3, 4) + member(33, 65, 3, 4);
  }
  const frameGroup = g('lantern-frame', frameMarkup);

  const body = `${defs}${g('lantern-base', base)}${emissive}${frameGroup}`;
  const at = (p: { x: number; y: number }) => ({ x: p.x, y: Y(p.y) });
  const windows = LANTERN_V2_WINDOWS.map(w => at(w)) as unknown as [Point, Point, Point];
  return {
    frame,
    body,
    anchors: {
      ...(support === 'hanging' ? { hang: { x: 24, y: 2 } } : {}),
      support: support === 'hanging' ? { x: 24, y: 2 } : { x: 24, y: frame.h },
      ribbon_tie: at({ x: 37.5, y: 22 }),
      light_core: at(LIGHT_CORE),
      mote_origin: at({ x: 24, y: 30 }),
      windows,
    },
    probes: { frame: at({ x: 12, y: 50 }), paper: at({ x: 30, y: 52 }), windows },
    parts,
  };
}
